
import { useState, useEffect } from "react";
import { CraftSupply, CraftIdea } from "./types";
import { trackToolUsage } from "@/utils/toolUsageTracker";
import { db } from "@/integrations/cloudflare/client";

const DEFAULT_SUPPLIES = [
  { id: "paper", name: "Colored Paper", checked: false },
  { id: "scissors", name: "Scissors", checked: false },
  { id: "glue", name: "Glue", checked: false },
  { id: "yarn", name: "Yarn", checked: false },
  { id: "buttons", name: "Buttons", checked: false },
  { id: "feltSheets", name: "Felt Sheets", checked: false },
  { id: "pompoms", name: "Pom-poms", checked: false },
  { id: "pipecleaners", name: "Pipe Cleaners", checked: false },
  { id: "googlyeyes", name: "Googly Eyes", checked: false },
  { id: "cottenballs", name: "Cotton Balls", checked: false },
];

export const useGeneratorLogic = (instanceId: string, postId?: string) => {
  const [supplies, setSupplies] = useState<CraftSupply[]>(DEFAULT_SUPPLIES);
  const [craftIdeas, setCraftIdeas] = useState<string[]>([]);
  const [usageCount, setUsageCount] = useState(0);
  const [curatedCrafts, setCuratedCrafts] = useState<CraftIdea[]>([]);

  useEffect(() => {
    const savedCount = localStorage.getItem(`craftGenerator_${instanceId}_count`);
    if (savedCount) {
      setUsageCount(parseInt(savedCount, 10));
    }
    
    // Fetch curated craft ideas
    const fetchCuratedCrafts = async () => {
      try {
        const { data, error } = await db
          .from('craft_ideas')
          .select('*');
          
        if (error) throw error;
        setCuratedCrafts(data || []);
      } catch (error) {
        console.error('Error fetching curated craft ideas:', error);
      }
    };
    
    fetchCuratedCrafts();
  }, [instanceId]);

  const toggleSupply = (id: string) => {
    setSupplies(supplies.map(supply => 
      supply.id === id ? { ...supply, checked: !supply.checked } : supply
    ));
  };

  const addCustomSupply = (name: string) => {
    if (name.trim() !== "") {
      const newId = `custom_${Date.now()}`;
      setSupplies([...supplies, { 
        id: newId, 
        name: name.trim(), 
        checked: true 
      }]);
    }
  };

  const removeSupply = (id: string) => {
    setSupplies(supplies.filter(supply => supply.id !== id));
  };

  const generateIdeas = () => {
    const selectedSupplies = supplies.filter(supply => supply.checked);
    
    if (selectedSupplies.length === 0) {
      setCraftIdeas(["Please select at least one craft supply!"]);
      return;
    }

    const newCount = usageCount + 1;
    setUsageCount(newCount);
    localStorage.setItem(`craftGenerator_${instanceId}_count`, newCount.toString());
    trackToolUsage("craftGenerator", instanceId, postId);

    // Get selected supply names
    const selectedSupplyNames = selectedSupplies.map(supply => supply.name);
    
    // Check for matching curated crafts first
    const matchingCrafts = curatedCrafts.filter(craft => {
      // Find crafts where at least one required supply is in the selected supplies
      return craft.required_supplies.some(supply => 
        selectedSupplyNames.includes(supply)
      );
    });
    
    // If we have matching curated crafts, prioritize those
    if (matchingCrafts.length > 0) {
      // Sort by how many matching supplies they have (most matches first)
      matchingCrafts.sort((a, b) => {
        const aMatches = a.required_supplies.filter(s => selectedSupplyNames.includes(s)).length;
        const bMatches = b.required_supplies.filter(s => selectedSupplyNames.includes(s)).length;
        return bMatches - aMatches;
      });
      
      // Take up to 3 matching crafts
      const selectedCrafts = matchingCrafts.slice(0, 3);
      
      // Format them for display
      const formattedCrafts = selectedCrafts.map(craft => {
        let idea = `${craft.title}`;
        
        if (craft.difficulty || craft.time_required) {
          idea += " (";
          if (craft.difficulty) {
            idea += `${craft.difficulty} difficulty`;
          }
          if (craft.difficulty && craft.time_required) {
            idea += ", ";
          }
          if (craft.time_required) {
            idea += `${craft.time_required}`;
          }
          idea += ")";
        }
        
        idea += ": " + craft.instructions;
        
        return idea;
      });
      
      // If we have fewer than 3 curated matches, add some generated ones
      if (formattedCrafts.length < 3) {
        import("@/utils/craftIdeasData").then(({ generateCraftIdeas }) => {
          const generatedIdeas = generateCraftIdeas(selectedSupplies);
          const combinedIdeas = [...formattedCrafts, ...generatedIdeas.slice(0, 3 - formattedCrafts.length)];
          setCraftIdeas(combinedIdeas);
        });
      } else {
        setCraftIdeas(formattedCrafts);
      }
    } else {
      // Fallback to generated ideas if no curated matches
      import("@/utils/craftIdeasData").then(({ generateCraftIdeas }) => {
        const ideas = generateCraftIdeas(selectedSupplies);
        setCraftIdeas(ideas);
      });
    }
  };

  const resetGenerator = () => {
    setSupplies(supplies.map(supply => ({ ...supply, checked: false })));
    setCraftIdeas([]);
  };

  return {
    supplies,
    craftIdeas,
    usageCount,
    defaultSupplyIds: DEFAULT_SUPPLIES.map(supply => supply.id),
    toggleSupply,
    addCustomSupply,
    removeSupply,
    generateIdeas,
    resetGenerator
  };
};
